import React, { useState, useEffect } from "react";
import {
  useTable,
  useFilters,
  useSortBy,
  usePagination,
  useGlobalFilter,
} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faEllipsisV,
  faEye,
  faFileInvoice,
  faTimesCircle,
  faCheckCircle,
  faPlus,
  faPen,
} from "@fortawesome/free-solid-svg-icons";
import apiClient from "../utils/apiClient";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment/moment";

const GlobalFilter = ({ filter, setFilter }) => (
  <div className="w-100">
    <input
      value={filter || ""}
      className="form-control"
      placeholder="Search..."
      onChange={(e) => setFilter(e.target.value || undefined)}
    />
  </div>
);
const IssueSlipTable = ({ accessOfDepartments, permissions }) => {
  const [activeTab, setActiveTab] = useState("table1");
  const navigate = useNavigate();
  const handleRequestView = (row) => {
    navigate("../../IssueRequestPreview", { state: { id: row.id } });
  };
  const handleIssueSlipView = (row) => {
    navigate("../../IssueSlipPreview", { state: { id: row.id } });
  };
  const [issueRequests, setIssueRequests] = useState([]);
  const [issueSlips, setIssueSlips] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
  
    Promise.all([
      apiClient.get(`${SERVER_BASE}/getIssueRequests`),                 // IR Pending
      apiClient.get(`${SERVER_BASE}/getPendingIssueFromRequirement`),  // PR Pending
      apiClient.get(`${SERVER_BASE}/getIssueSlips`)                    // Completed Slips
    ])
      .then(([reqRes, prRes, slipRes]) => {
  
        // ------------------ TABLE 1 : PENDING ISSUE (IR + PR) ------------------
  
        // filter Issue Requests by department access
        const filteredRequests = reqRes.data
          .filter(
            (request) =>
              accessOfDepartments.includes(request.receivingDepartment) ||
              accessOfDepartments.includes(request.issuerDepartment)
          )
          .map(r => ({
            ...r,
            type: "IR"
          }));
  
        // filter Project Requirements by department access
        const filteredPR = prRes.data
          .filter(
            (req) =>
              accessOfDepartments.includes(req.receivingDepartment) ||
              accessOfDepartments.includes(req.issuerDepartment)
          )
          .map(r => ({
            ...r,
            type: "PR"
          }));
  
        // 🔹 Merge both in ONE pending table
        const mergedPending = [...filteredRequests, ...filteredPR];
  
        setIssueRequests(mergedPending);
  
        // ------------------ TABLE 2 : COMPLETED ISSUE SLIPS ------------------
  
        const filteredSlips = slipRes.data.filter(
          (slip) =>
            accessOfDepartments.includes(slip.receivingDepartment) ||
            accessOfDepartments.includes(slip.issuingDepartment)
        );
  
        setIssueSlips(filteredSlips);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  
  }, [accessOfDepartments]);
  


  const handleRejectRequest = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to reject this request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Reject it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await apiClient.post(
            `${SERVER_BASE}/rejectIssueRequest`,
            { id }
          );

          Swal.fire("Rejected!", "The request has been rejected.", "success");

          // refresh table or update state if needed
          // example: fetchData();
        } catch (err) {
          console.error(err);
          Swal.fire("Error!", "Something went wrong while rejecting.", "error");
        }
      }
    });
  };

  const TableComponent = ({ columns, data }) => {
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      page,
      nextPage,
      previousPage,
      canNextPage,
      canPreviousPage,
      pageOptions,
      gotoPage,
      setPageSize,
      state,
      setGlobalFilter,
    } = useTable(
      {
        columns,
        data,
      },
      useFilters,
      useGlobalFilter,
      useSortBy,
      usePagination
    );

    const { pageIndex, pageSize, globalFilter } = state;

    return (
      <div>
        <div className="d-flex justify-content-between gap-2 mb-3">
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="form-select form-select-sm"
            style={{ width: "auto" }}
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </select>
        </div>
        <table {...getTableProps()} className="table table-striped" style={{ fontSize: '14px' }}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render("Header")}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " 🔽"
                          : " 🔼"
                        : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="pagination d-flex align-items-center justify-content-between ">
          {/* Left Section: Next and Previous Buttons */}

          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="btn btn-secondary"
          >
            Previous
          </button>
          <span>
            Page{" "}
            <strong>
              {pageIndex + 1} of {pageOptions.length}
            </strong>
          </span>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="btn btn-secondary"
          >
            Next
          </button>

          {/* Right Section: Page Size */}
        </div>
      </div>
    );
  };

  const Table1 = () => {
    const pendingRequests = React.useMemo(() => {
      return issueRequests.filter(
        (request) =>
          request.status === "Approved" ||
          request.status === "Partially Issued"
      );
    }, [issueRequests]);

    const columns = React.useMemo(
      () => [
        { Header: "RQ Number", accessor: "requestNumber" },
        { Header: "Requesting", accessor: "receivingDepartment" },
        { Header: "Issuing", accessor: "issuerDepartment" },
        {
          Header: "Project", accessor: "projectName", Cell: ({ value }) => {
            const display = value ? value : 'Regular';
            return display
          }
        },
        {
          Header: "Items",
          accessor: "items",
          Cell: ({ value }) => {
            if (!Array.isArray(value) || value.length === 0) return "-";

            return value
              .map(i => i.item || i.itemName || i.printName)
              .filter(Boolean)
              .join(", ");
          }
        },
        { Header: "Status", accessor: "status" },
        {
          Header: "Action",
          accessor: "action",
          Cell: ({ row }) => {
            return (
              permissions?.read && (
                <button
                  className="btn btn-sm btn-outline-success me-1"
                  onClick={() => {
                    const rowData = row.original;

                    if (rowData.type === "IR") {
                      // Normal Issue Request
                      navigate("../../IssueSlip", {
                        state: {
                          requestNumber: rowData.requestNumber,
                          type: "IR",
                        },
                      });
                    }

                    if (rowData.type === "PR") {
                      // Project Requirement
                      navigate("../../IssueSlip", {
                        state: {
                          projectRequirementId: rowData.id,
                          type: "PR",
                        },
                      });
                    }
                  }}

                >
                  <FontAwesomeIcon icon={faFileInvoice} /> Generate
                </button>
              )
            );
          },
        },
        {
          Header: "#",
          accessor: "actions",
          Cell: ({ row }) => (
            <div className="dropdown">
              {/* {permissions?.read && 
               
                  <button
                    className="btn btn-sm btn-outline-success me-1"
                    onClick={() => handleRequestView(row.original)}
                  > View
                  </button>
               
        } */}
              <button
                className="btn btn-light btn-sm"
                type="button"
                id={`dropdownMenuButton-${row.index}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby={`dropdownMenuButton-${row.index}`}
              >
                {permissions?.delete && (
                  <>

                    <li>
                      <button
                        className="dropdown-item text-danger"
                        onClick={() => handleRejectRequest(row.original.id)}
                      >
                        <FontAwesomeIcon icon={faTimesCircle} /> Reject Request
                      </button>
                    </li>
                  </>
                )}
                <li>
                  <hr className="dropdown-divider" />
                </li>

                {/* Created By / Updated By */}
                <li>
                  <span className="dropdown-item-text text-muted">
                    <FontAwesomeIcon icon={faPlus} />{" "}
                    {row.original.createdBy
                      ? `${row.original.createdBy} (${row.original.created_at
                        ? moment(row.original.created_at).format(
                          "DD-MMM-YY hh:mm A"
                        )
                        : ""
                      })`
                      : ""}
                  </span>
                </li>
                {row.original.updatedAt && (
                  <li>
                    <span className="dropdown-item-text text-muted">
                      <FontAwesomeIcon icon={faPen} />{" "}
                      {row.original.updatedAt
                        ? `${row.original.updatedBy} ${row.original?.updatedAt
                          ? moment(row.original?.updatedAt).format(
                            "DD-MMM-YY hh:mm A"
                          )
                          : ""
                        }`
                        : ""}
                    </span>
                  </li>
                )}
                {row.original.approvedBy && (
                  <li>
                    <span className="dropdown-item-text text-muted">
                      <FontAwesomeIcon icon={faCheckCircle} />{" "}
                      {row.original.approvedBy
                        ? `${row.original.approvedBy} ${row.original?.approvedAt
                          ? moment(row.original?.approvedAt).format(
                            "DD-MMM-YY hh:mm A"
                          )
                          : ""
                        }`
                        : ""}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          ),
        },
      ],
      []
    );

    return <TableComponent columns={columns} data={pendingRequests} />;
  };

  const Table2 = () => {
    const completedSlips = React.useMemo(() => {
      return issueSlips.filter(
        (slip) =>
          slip.status !== "Pending" &&
          slip.status !== "Approved"
      );
    }, [issueSlips]);


    const columns = React.useMemo(
      () => [
        { Header: "RQ Number", accessor: "requestNumber" },
        { Header: "IS Number", accessor: "issueSlipCode" },
        // { Header: 'RS Number', accessor: 'returnSlipCode' },
        { Header: "Requesting", accessor: "receivingDepartment" },
        { Header: "Issuing", accessor: "issuingDepartment" },
        {
          Header: "Project", accessor: "projectName", Cell: ({ value }) => {
            const display = value ? value : 'Regular';
            return display
          }
        },
        {
          Header: "Items",
          accessor: "interDepartmentIssueSlipItems",
          Cell: ({ value }) => {
            if (!Array.isArray(value) || value.length === 0) return "-";

            return value
              .map(i => i.item || i.itemName || i.printName)
              .filter(Boolean)
              .join(", ");
          }
        },
        { Header: "Status", accessor: "status" },
        {
          Header: "Preview",
          accessor: "Preview",
          Cell: ({ row }) => {
            return (
              permissions?.read && (
                <button
                  className="btn btn-sm btn-outline-success me-1"
                  onClick={() => handleIssueSlipView(row.original)}
                >
                  {" "}
                  View
                </button>
              )
            );
          },
        },
        {
          Header: "#",
          accessor: "actions",
          Cell: ({ row }) =>
            row.original.status !== "Rejected" ? (
              <div className="dropdown">
                <button
                  className="btn btn-light"
                  type="button"
                  id={`dropdownMenuButton-${row.index}`}
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </button>
                <ul
                  className="dropdown-menu dropdown-menu-end"
                  aria-labelledby={`dropdownMenuButton-${row.index}`}
                >
                  <li>
                    <hr className="dropdown-divider" />
                  </li>

                  {/* Created By / Updated By */}
                  <li>
                    <span className="dropdown-item-text text-muted">
                      <FontAwesomeIcon icon={faPlus} />{" "}
                      {row.original.createdBy
                        ? `${row.original.createdBy} (${row.original.created_at
                          ? moment(row.original.created_at).format(
                            "DD-MMM-YY hh:mm A"
                          )
                          : ""
                        })`
                        : ""}
                    </span>
                  </li>
                  {row.original.updatedAt && (
                    <li>
                      <span className="dropdown-item-text text-muted">
                        <FontAwesomeIcon icon={faPen} />{" "}
                        {row.original.updatedAt
                          ? `${row.original.updatedBy} (${row.original?.updatedAt
                            ? moment(row.original?.updatedAt).format(
                              "DD-MMM-YY hh:mm A"
                            )
                            : ""
                          })`
                          : ""}
                      </span>
                    </li>
                  )}
                  {row.original.approvedBy && (
                    <li>
                      <span className="dropdown-item-text text-muted">
                        <FontAwesomeIcon icon={faCheckCircle} />{" "}
                        {row.original.approvedBy
                          ? `${row.original.approvedBy} (${row.original?.approvedAt
                            ? moment(row.original?.approvedAt).format(
                              "DD-MMM-YY hh:mm A"
                            )
                            : ""
                          })`
                          : ""}
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            ) : null,
        },
      ],
      []
    );

    return <TableComponent columns={columns} data={completedSlips} />;
  };

  return (
    <div className="formcontainer mt-5">
      <h3 className="mb-3">Issue Slip</h3>
      {permissions?.read && (
        <>
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "table1" ? "active" : ""}`}
                onClick={() => setActiveTab("table1")}
              >
                Pending Issue Slips
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "table2" ? "active" : ""}`}
                onClick={() => setActiveTab("table2")}
              >
                Completed Issue Slips
              </button>
            </li>
          </ul>
          <div className="tab-content mt-4">
            {activeTab === "table1" && (
              <div className="tab-pane fade show active">
                <Table1 />
              </div>
            )}
            {activeTab === "table2" && (
              <div className="tab-pane fade show active">
                <Table2 />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default IssueSlipTable;
